import { format } from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";

const ISO_DAY_FORMAT = "yyyy-MM-dd";

// A bare calendar day, as date columns and the dashboard's own days look.
const ISO_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// Built once: every payment request of a list goes through it. The parts are
// picked by type, so the locale's own day order does not matter.
const BRUSSELS_DAY_PARTS = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Brussels",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

// A moment's calendar day in the user's own time zone, as "YYYY-MM-DD".
// toISOString() gives the UTC day instead, which in Belgium is still the day
// before until 01:00 (winter time) or 02:00 (summer time): a statement made
// or a settlement confirmed just after midnight was dated yesterday.
export const toLocalIsoDay = (date: Date): string =>
  format(date, ISO_DAY_FORMAT);

// Today in the user's own time zone, as "YYYY-MM-DD".
export const todayIsoDay = (): string => toLocalIsoDay(new Date());

// The Brussels calendar day of a moment the API sends (an ISO datetime string
// or a Date), as "YYYY-MM-DD": the day the backend also stamps on a cost split
// paid through that request. Slicing the string gives the UTC day instead, so
// a payment request sent or paid between 00:00 and 02:00 showed the day
// before. A bare "YYYY-MM-DD" is a calendar day already and stays as it is.
export const toBrusselsIsoDay = (
  value: string | Date | null | undefined,
): string | undefined => {
  if (!value) return undefined;
  if (typeof value === "string" && ISO_DAY_PATTERN.test(value)) return value;
  const moment = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(moment.getTime())) return undefined;
  const { year, month, day } = Object.fromEntries(
    BRUSSELS_DAY_PARTS.formatToParts(moment).map(part => [
      part.type,
      part.value,
    ]),
  );
  return `${year}-${month}-${day}`;
};
